import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { ChevronRight, ArrowLeft, BookOpen } from "lucide-react";
import PromoSidebar from "../components/PromoSidebar";

const API = window.location.origin;

const SUBJECT_ICONS = {
  science: "🔬", mathematics: "📐", hindi: "🇮🇳", english: "📖",
  social_studies: "🗺️", pedagogy: "🎓", physics: "⚛️", chemistry: "🧪",
  biology: "🦠", history: "🏛️", geography: "🌍", economics: "📊",
  general_knowledge: "💡", reasoning: "🧠", arithmetic: "🔢",
  child_development: "👶", evs: "🌱", language_hindi: "📝", language_english: "✍️",
  science_paper2: "🔬", social_science_paper2: "🗺️", general_awareness: "🌐",
  subject_knowledge: "📚", subject_concerned: "📚", commerce: "💼"
};

const DIFF_COLORS = { easy: "text-emerald-400", medium: "text-amber-400", hard: "text-red-400" };

export default function QuestionBankExam() {
  const { examType } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/question-bank/public/${examType}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [examType]);

  const examLabel = data?.exam_label || examType?.toUpperCase();
  const pageTitle = `${examLabel} MCQ Questions [Free] - Descriptive Test`;
  const totalQ = data?.subjects?.reduce((s, x) => s + x.total_questions, 0) || 0;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={`Practice ${totalQ}+ ${examLabel} MCQ questions topic-wise for EMRS, KVS, NVS Tier-2 exam 2026. Free questions with answers and detailed AI explanations. All subjects covered. Powered by Descriptive Test.`} />
        <meta name="keywords" content={`${examLabel} questions, ${examType} MCQ, ${examType} practice questions, EMRS ${examType} MCQ, KVS ${examType} questions, NVS ${examType} practice, ${examType} tier 2 questions, ${examType} objective questions, teaching exam MCQ, ${examType} previous year questions, Descriptive Test`} />
        <link rel="canonical" href={`https://descriptivetest.com/question-bank/${examType}`} />
      </Helmet>

      <div className="min-h-screen bg-[#0a0d14] text-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0a0d14] via-indigo-950/30 to-[#0a0d14] border-b border-white/5">
          <div className="max-w-5xl mx-auto px-4 py-10">
            <div className="flex items-center gap-3 mb-5">
              <Link to="/dashboard" data-testid="home-btn" className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-lg transition-colors text-slate-300 hover:text-white font-medium text-sm">
                <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
              </Link>
              <Link to="/question-bank" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm transition-colors">
                All Exams
              </Link>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <span className="bg-indigo-500/20 text-indigo-300 text-xs font-bold px-3 py-1 rounded-full border border-indigo-500/30">
                {examType?.toUpperCase()}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-2">{examLabel}</h1>
            <p className="text-slate-400 text-sm">
              <span className="text-indigo-400 font-bold">{totalQ.toLocaleString()}</span> questions across {data?.subjects?.length || 0} subjects
            </p>
            <p className="text-xs text-slate-600 mt-1">Powered by <span className="text-indigo-500">Descriptive Test</span></p>
          </div>
        </div>

        {/* Subjects Grid + Sidebar */}
        <div className="max-w-6xl mx-auto px-4 py-8 flex gap-8 items-start">
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[...Array(6)].map((_, i) => <div key={i} className="h-28 bg-white/5 rounded-xl animate-pulse" />)}
              </div>
            ) : !data ? (
              <div className="text-center py-16 text-slate-400">
                <p>Exam not found. <Link to="/question-bank" className="text-indigo-400">Go back</Link></p>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-bold text-white mb-6">Select Subject</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.subjects.map(subj => {
                    const icon = SUBJECT_ICONS[subj.subject] || "📚";
                    const hasQ = subj.total_questions > 0;
                    return (
                      <Link
                        key={subj.subject}
                        to={`/question-bank/${examType}/${subj.subject}`}
                        data-testid={`subject-card-${subj.subject}`}
                        className={`group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-indigo-500/30 rounded-xl p-5 transition-all duration-200 ${!hasQ ? "opacity-60" : ""}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <span className="text-2xl">{icon}</span>
                          <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-indigo-400 group-hover:translate-x-1 transition-all" />
                        </div>
                        <h3 className="font-semibold text-slate-100 group-hover:text-white mb-1 text-sm">
                          {subj.label}
                        </h3>
                        <div className="flex gap-3 text-xs text-slate-500">
                          <span className={hasQ ? "text-emerald-400 font-semibold" : ""}>
                            {subj.total_questions} Questions
                          </span>
                          <span>{subj.total_topics} Topics</span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <PromoSidebar />
        </div>

        <div className="border-t border-white/5 py-6 text-center mt-8">
          <p className="text-slate-600 text-xs">© 2026 <Link to="/" className="text-indigo-500">Descriptive Test</Link></p>
        </div>
      </div>
    </>
  );
}
